/**
 * @file color-groups.ts
 * @description Группы вкладки «Оформление → Цвета» (phase19.16, SB122;
 * uiux-settings-groups §5 шаг 3): роли группы по реестру
 * `SITE_COLOR_ROLE_INFO`, короткая сводка свёрнутой группы («по умолчанию»,
 * «свои: 1 из 3», «свои») и признак предупреждения о контрасте.
 *
 * Состав групп и подписи — из `@chaiberi/shared` (`SITE_COLOR_GROUP_LABELS`):
 * новая группа или роль — запись в реестре, не ветка здесь.
 */

import {
  DEFAULT_SITE_THEME,
  SITE_COLOR_GROUP_LABELS,
  SITE_COLOR_ROLE_INFO,
  SITE_COLOR_ROLES,
  type SiteColorGroup,
  type SiteColorRole,
  type SiteColors,
} from "@chaiberi/shared";

import { roleContrast } from "./color-roles";

/** Группы в порядке реестра. */
export const SITE_COLOR_GROUPS = Object.keys(SITE_COLOR_GROUP_LABELS) as SiteColorGroup[];

/** Короткие подписи пилюль оглавления (до 12 знаков); нет — заголовок группы. */
export const COLOR_GROUP_TOC: Readonly<Partial<Record<SiteColorGroup, string>>> = {
  surface: "Фон",
  dark: "Тёмные",
};

/** Роли группы в порядке реестра ролей. */
export function colorGroupRoles(group: SiteColorGroup): SiteColorRole[] {
  return SITE_COLOR_ROLES.filter((role) => SITE_COLOR_ROLE_INFO[role].group === group);
}

export interface ColorGroupView {
  roles: SiteColorRole[];
  /** Сводка свёрнутой группы: «по умолчанию», «свои: 1 из 3», «свои» (+ «контраст ниже нормы»). */
  summary: string;
  /** Хотя бы у одной роли контраст ниже нормы — «!» в заголовке группы. */
  warning: boolean;
}

/** Сводка и предупреждение группы цветов. */
export function colorGroupView(group: SiteColorGroup, colors: SiteColors): ColorGroupView {
  const roles = colorGroupRoles(group);
  const own = roles.filter((role) => colors[role].toLowerCase() !== DEFAULT_SITE_THEME.colors[role].toLowerCase()).length;
  const warning = roles.some((role) => {
    const contrast = roleContrast(colors, role);
    return contrast !== null && contrast.level !== "ok";
  });
  const base = own === 0 ? "по умолчанию" : own === roles.length ? "свои" : `свои: ${own} из ${roles.length}`;
  return { roles, summary: warning ? `${base} · контраст ниже нормы` : base, warning };
}
